/**
 * RiskRegisterPage.tsx — Live editable reservoir-aeration risk register
 *
 * Persisted via sessionPersistence. Each row carries likelihood × impact
 * (raw and residual) plus controls and owner. Exportable to PDF.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ShieldAlert, ArrowLeft, Plus, Trash2, Printer, Save,
} from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, savePersisted } from '@/lib/sessionPersistence';
import { generateRiskRegisterPDF } from '@/lib/reservoirPdf';
import { toast } from 'sonner';

const KEY = 'rb_risk_register_v1';

export interface RiskRow {
  id: string;
  ref: string;
  category: 'engineering' | 'safety' | 'environmental' | 'commercial' | 'regulatory' | 'operational';
  description: string;
  cause: string;
  consequence: string;
  rawLikelihood: number;   // 1–5
  rawImpact: number;       // 1–5
  controls: string;
  resLikelihood: number;   // 1–5
  resImpact: number;       // 1–5
  owner: string;
  status: 'open' | 'mitigated' | 'closed';
}

const DEFAULT_REGISTER: RiskRow[] = [
  {
    id: 'r1', ref: 'R-001', category: 'engineering',
    description: 'Sediment resuspension on commissioning',
    cause: 'Initial plume impingement exceeds Shields critical shear in worst-case bed zone',
    consequence: 'Turbidity excursion into abstraction stream; possible DWI notification',
    rawLikelihood: 3, rawImpact: 4,
    controls: 'Phased commissioning, plume Shields ratio check at every module location, recovery cleaning protocol',
    resLikelihood: 1, resImpact: 3,
    owner: 'Design lead', status: 'open',
  },
  {
    id: 'r2', ref: 'R-002', category: 'engineering',
    description: 'Under-aeration in worst-case summer profile',
    cause: 'Clean-water SOTE used in sizing without surfactant correction',
    consequence: 'Failure to achieve DO target during stratification, possible algal bloom',
    rawLikelihood: 3, rawImpact: 5,
    controls: 'Vasconcelos surfactant penalty applied, advanced module-count multiplier, seasonal worst-case profile verified',
    resLikelihood: 1, resImpact: 3,
    owner: 'Design lead', status: 'mitigated',
  },
  {
    id: 'r3', ref: 'R-003', category: 'environmental',
    description: 'Phosphate release from disturbed sediment',
    cause: 'Iron-bound P released as redox conditions change near disturbed bed',
    consequence: 'Short-term P spike, downstream WTW filter loading',
    rawLikelihood: 2, rawImpact: 3,
    controls: 'Bed-stable diffuser configuration, gradual ramp-up commissioning, P monitoring at intake',
    resLikelihood: 1, resImpact: 2,
    owner: 'Environmental lead', status: 'open',
  },
  {
    id: 'r4', ref: 'R-004', category: 'safety',
    description: 'DSEAR — blower compound zone classification',
    cause: 'Oil-injected blower with hot surfaces near combustible vents',
    consequence: 'Fire / explosion risk in plant room',
    rawLikelihood: 1, rawImpact: 5,
    controls: 'Oil-free blower selection, DSEAR assessment by competent person, ventilation design',
    resLikelihood: 1, resImpact: 4,
    owner: 'Safety lead', status: 'open',
  },
  {
    id: 'r5', ref: 'R-005', category: 'operational',
    description: 'Blower outage during stratification onset',
    cause: 'Single blower failure with no redundancy',
    consequence: 'Hypolimnion anoxia within days; restart triggers turbidity event',
    rawLikelihood: 2, rawImpact: 5,
    controls: 'N+1 redundancy, VSD turn-down, hot-standby auto-changeover, 24/7 SCADA alarm',
    resLikelihood: 1, resImpact: 3,
    owner: 'Operations lead', status: 'open',
  },
  {
    id: 'r6', ref: 'R-006', category: 'engineering',
    description: 'Diffuser fouling drift beyond modelled curve',
    cause: 'Higher-than-expected iron / calcium / NOM loading',
    consequence: 'SOTE decay, pressure rise, premature membrane replacement',
    rawLikelihood: 3, rawImpact: 3,
    controls: 'Pilot trial fouling tracking, recovery-cleaning regime, ten-year membrane creep modelling',
    resLikelihood: 2, resImpact: 2,
    owner: 'Design lead', status: 'open',
  },
  {
    id: 'r7', ref: 'R-007', category: 'regulatory',
    description: 'Late regulatory query (DWI / EA) delays commissioning',
    cause: 'Consent documentation prepared sequentially rather than in parallel',
    consequence: 'Commissioning slips an entire stratification season',
    rawLikelihood: 3, rawImpact: 4,
    controls: 'Parallel consent programme from week 1, early engagement with regulators, panel-engineer review',
    resLikelihood: 1, resImpact: 3,
    owner: 'Project manager', status: 'open',
  },
  {
    id: 'r8', ref: 'R-008', category: 'commercial',
    description: 'Embedded power capacity at embankment insufficient',
    cause: 'Available supply not audited before blower specification',
    consequence: 'Costly mains upgrade or smaller blower than design',
    rawLikelihood: 3, rawImpact: 4,
    controls: 'Power audit during week 1-2 mobilisation, harmonics survey, alternative supply route assessment',
    resLikelihood: 1, resImpact: 2,
    owner: 'Project manager', status: 'open',
  },
  {
    id: 'r9', ref: 'R-009', category: 'safety',
    description: 'Diver / ROV during installation in shipping channel',
    cause: 'Unverified submerged legacy infrastructure',
    consequence: 'Diver injury, equipment loss',
    rawLikelihood: 2, rawImpact: 5,
    controls: 'Pre-installation ROV survey, dive risk assessment, permit-to-work, comms link to surface',
    resLikelihood: 1, resImpact: 4,
    owner: 'Safety lead', status: 'open',
  },
  {
    id: 'r10', ref: 'R-010', category: 'environmental',
    description: 'Bird / wildlife disturbance during installation',
    cause: 'Installation during nesting season near reservoir margin',
    consequence: 'Wildlife disturbance, possible EA enforcement',
    rawLikelihood: 2, rawImpact: 3,
    controls: 'Ecological survey, scheduling outside breeding season where practical, EA liaison',
    resLikelihood: 1, resImpact: 2,
    owner: 'Environmental lead', status: 'open',
  },
];

function score(l: number, i: number): number { return l * i; }

function bandColour(s: number): string {
  if (s >= 15) return 'bg-red-900/40 text-red-300 border-red-700';
  if (s >= 8)  return 'bg-amber-900/40 text-amber-300 border-amber-700';
  if (s >= 4)  return 'bg-yellow-900/40 text-yellow-300 border-yellow-700';
  return 'bg-emerald-900/40 text-emerald-300 border-emerald-700';
}

function uid() { return 'r' + Math.random().toString(36).slice(2, 8); }

export default function RiskRegisterPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<RiskRow[]>(() => {
    const persisted = loadPersisted<{ rows?: RiskRow[] }>(KEY, { rows: DEFAULT_REGISTER });
    return persisted.rows && persisted.rows.length ? persisted.rows : DEFAULT_REGISTER;
  });
  const [filter, setFilter] = useState<string>('all');

  useEffect(() => {
    savePersisted(KEY, { rows });
  }, [rows]);

  const filtered = useMemo(
    () => filter === 'all' ? rows : rows.filter(r => r.category === filter),
    [rows, filter]
  );

  const summary = useMemo(() => {
    const total = rows.length;
    const high = rows.filter(r => score(r.resLikelihood, r.resImpact) >= 15).length;
    const med  = rows.filter(r => { const s = score(r.resLikelihood, r.resImpact); return s >= 8 && s < 15; }).length;
    const low  = rows.filter(r => score(r.resLikelihood, r.resImpact) < 8).length;
    return { total, high, med, low };
  }, [rows]);

  const update = (id: string, patch: Partial<RiskRow>) =>
    setRows(rs => rs.map(r => r.id === id ? { ...r, ...patch } : r));
  const remove = (id: string) => setRows(rs => rs.filter(r => r.id !== id));
  const add = () => setRows(rs => [...rs, {
    id: uid(), ref: `R-${String(rs.length + 1).padStart(3, '0')}`,
    category: 'engineering',
    description: 'New risk', cause: '', consequence: '',
    rawLikelihood: 3, rawImpact: 3, controls: '',
    resLikelihood: 2, resImpact: 2, owner: '', status: 'open',
  }]);

  const exportPDF = () => {
    generateRiskRegisterPDF({ rows });
    toast.success('Risk register PDF generated');
  };

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={ShieldAlert}
        title="Reservoir Aeration — Risk Register"
        subtitle="Live engineering, safety, environmental, commercial and regulatory risks for the Barcombe scheme"
        actions={
          <div className="flex items-center gap-2">
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <button onClick={add}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <Plus className="w-4 h-4" /> Add risk
            </button>
            <button onClick={() => { savePersisted(KEY, { rows }); toast.success('Saved'); }}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <Save className="w-4 h-4" /> Save
            </button>
            <button onClick={exportPDF}
              className="inline-flex items-center gap-2 rounded-lg bg-rb-brand px-3 py-1.5 text-sm font-semibold text-white hover:bg-rb-brand/90">
              <Printer className="w-4 h-4" /> Export PDF
            </button>
          </div>
        }
      />

      {/* Summary tiles */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Total risks</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{summary.total}</div>
        </div>
        <div className="rounded-xl border border-red-700 bg-red-900/20 p-4">
          <div className="text-xs text-red-300 uppercase tracking-wider">High (residual ≥ 15)</div>
          <div className="text-2xl font-bold text-red-200 mt-1">{summary.high}</div>
        </div>
        <div className="rounded-xl border border-amber-700 bg-amber-900/20 p-4">
          <div className="text-xs text-amber-300 uppercase tracking-wider">Medium (residual 8–14)</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">{summary.med}</div>
        </div>
        <div className="rounded-xl border border-emerald-700 bg-emerald-900/20 p-4">
          <div className="text-xs text-emerald-300 uppercase tracking-wider">Low (residual &lt; 8)</div>
          <div className="text-2xl font-bold text-emerald-200 mt-1">{summary.low}</div>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        {['all', 'engineering', 'safety', 'environmental', 'commercial', 'regulatory', 'operational'].map(c => (
          <button key={c} onClick={() => setFilter(c)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              filter === c ? 'bg-rb-brand text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
            }`}>
            {c}
          </button>
        ))}
      </div>

      {/* Rows */}
      <div className="space-y-3">
        {filtered.map((r) => {
          const rawScore = score(r.rawLikelihood, r.rawImpact);
          const resScore = score(r.resLikelihood, r.resImpact);
          return (
            <div key={r.id} className={`rounded-xl border bg-slate-900 p-4 ${bandColour(resScore)}`}>
              <div className="flex items-start gap-3 mb-2">
                <input value={r.ref}
                  onChange={e => update(r.id, { ref: e.target.value })}
                  className="w-20 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 font-mono" />
                <select value={r.category}
                  onChange={e => update(r.id, { category: e.target.value as RiskRow['category'] })}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100">
                  <option value="engineering">Engineering</option>
                  <option value="safety">Safety</option>
                  <option value="environmental">Environmental</option>
                  <option value="commercial">Commercial</option>
                  <option value="regulatory">Regulatory</option>
                  <option value="operational">Operational</option>
                </select>
                <input value={r.description} placeholder="Description"
                  onChange={e => update(r.id, { description: e.target.value })}
                  className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1 text-sm text-slate-100 font-semibold" />
                <select value={r.status}
                  onChange={e => update(r.id, { status: e.target.value as RiskRow['status'] })}
                  className="bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100">
                  <option value="open">Open</option>
                  <option value="mitigated">Mitigated</option>
                  <option value="closed">Closed</option>
                </select>
                <button onClick={() => remove(r.id)} className="text-slate-500 hover:text-red-400">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                <div>
                  <label className="text-slate-400 block mb-1">Cause</label>
                  <textarea value={r.cause}
                    onChange={e => update(r.id, { cause: e.target.value })}
                    rows={2}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
                </div>
                <div>
                  <label className="text-slate-400 block mb-1">Consequence</label>
                  <textarea value={r.consequence}
                    onChange={e => update(r.id, { consequence: e.target.value })}
                    rows={2}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
                </div>
                <div className="md:col-span-2">
                  <label className="text-slate-400 block mb-1">Controls / mitigation</label>
                  <textarea value={r.controls}
                    onChange={e => update(r.id, { controls: e.target.value })}
                    rows={2}
                    className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-slate-100" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3 mt-3">
                <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Raw risk</div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-300">L</label>
                    <input type="number" min={1} max={5} value={r.rawLikelihood}
                      onChange={e => update(r.id, { rawLikelihood: Number(e.target.value) })}
                      className="w-10 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                    <label className="text-xs text-slate-300">I</label>
                    <input type="number" min={1} max={5} value={r.rawImpact}
                      onChange={e => update(r.id, { rawImpact: Number(e.target.value) })}
                      className="w-10 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                    <span className={`ml-auto px-2 py-0.5 rounded text-xs font-bold border ${bandColour(rawScore)}`}>{rawScore}</span>
                  </div>
                </div>
                <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Residual</div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-300">L</label>
                    <input type="number" min={1} max={5} value={r.resLikelihood}
                      onChange={e => update(r.id, { resLikelihood: Number(e.target.value) })}
                      className="w-10 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                    <label className="text-xs text-slate-300">I</label>
                    <input type="number" min={1} max={5} value={r.resImpact}
                      onChange={e => update(r.id, { resImpact: Number(e.target.value) })}
                      className="w-10 bg-slate-900 border border-slate-700 rounded px-1 py-0.5 text-xs text-slate-100" />
                    <span className={`ml-auto px-2 py-0.5 rounded text-xs font-bold border ${bandColour(resScore)}`}>{resScore}</span>
                  </div>
                </div>
                <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-1">Owner</div>
                  <input value={r.owner}
                    onChange={e => update(r.id, { owner: e.target.value })}
                    className="w-full bg-slate-900 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
